import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import Select from "react-select";
import bannerImage from "../../../images/Gaudium washcare banner.png";
import {
  deleteCartItem,
  loadingCartChange,
  updateProductSize,
  fetchBundleById,
  addToCart,
  getCartDetail,
  fetchAllProducts
} from "../../../actions/product";
import ConfirmModal from "../../layout/ConfirmModal";
import FullPageSpinner from "../../layout/FullPageSpinner";
import {
  calculateCartWeight,
  calculateItemAdditionWeight,
  getMaxConfiguredWeight,
} from "../../../utils/weightHelper";
import "./BundleDetailsPage.css";
import { 
  ArrowLeft, 
  User, 
  Calendar, 
  GraduationCap, 
  Home, 
  Minus, 
  Plus, 
  Download, 
  ShoppingCart, 
  CheckCircle, 
  Trash2, 
  Package,
  Info
} from "lucide-react";

const BundleDetailsPage = () => {
  const { bundleId, studentId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { user, cartData } = useSelector((state) => ({
    user: state.auth.user,
    cartData: state.product,
  }));

  const isAdminSession = !!sessionStorage.getItem("adminToken");

  const [bundle, setBundle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity] = useState(1);
  const [productSizes, setProductSizes] = useState({});
  // const [loadingStates, setLoadingStates] = useState({});
  const [pendingSizes, setPendingSizes] = useState({});
  const [quantities, setQuantities] = useState({});
  const [removeItemId, setRemoveItemId] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [cartItemProducts, setCartItemProducts] = useState([]);
  const [bundleTotalPrice, setBundleTotalPrice] = useState(0);
  const [allProducts, setAllProducts] = useState([]);
  const [showAddProduct, setShowAddProduct] = useState(false);

  useEffect(() => {
    const getBundleDetails = async () => {
      setLoading(true);
      try {
        const numericBundleId = Number(bundleId);
        const numericStudentId = Number(studentId);
        if (!isNaN(numericBundleId) && !isNaN(numericStudentId)) {
          const data = await fetchBundleById(numericBundleId, numericStudentId);
          if (data) setBundle(data);
        }
      } catch (error) {
        console.error("Error fetching bundle:", error);
      } finally {
        setLoading(false);
      }
    };

    if (bundleId && studentId) {
      getBundleDetails();
    }
  }, [bundleId, studentId]);

  useEffect(() => {
      if (!bundle) return;

      const numericStudentId = Number(studentId);

      const cartItem = cartData?.items?.find(
        item =>
          item.bundleId === bundle.bundle_id &&
          Number(item.studentId) === numericStudentId
      );

      // Check for extra products in cart that are NOT in bundle.products
      if (cartItem?.products?.length > 0) {
        const extraProducts = cartItem.products.filter(
          cp => !bundle.products.some(bp => bp.product_id === cp.productId)
        );

        if (extraProducts.length > 0) {
          const transformedExtra = extraProducts.map(cp => {
            const p = cp.product; // Joined by backend
            return {
              product_id: cp.productId,
              product_name: p?.name || "Added Item",
              product_display_name: p?.display_name || p?.name || "Added Item",
              unit_price: Number(cp.unitPrice),
              bundle_price: Number(cp.bundle_price),
              quantity: 0, // Base quantity is 0 for items added manually
              max_quantity: p?.max_quantity || 10,
              order: p?.order || 99,
              optional: true,
              selection_group: false,
              size_chart: p?.sizeChart,
              product_category: p?.category || "Others",
              product_category_order: p?.category_order || 999,
              availableSizes: p?.availableSizes?.length > 0 ? p.availableSizes : ["Free-NA"],
              size: cp.size
            };
          });

          setBundle(prev => ({
            ...prev,
            products: [...(prev.products || []), ...transformedExtra]
          }));
          return;
        }
      }

      const initialSizes = {};
      const initialQuantities = {};
      const products = [];
      let bundlePrice = 0;

      bundle?.products?.forEach((product) => {
        if (product?.availableSizes?.length > 0) {
          initialSizes[product.product_id] = product.size || "";
        } else {
          product.availableSizes = ["Free-NA"];
          initialSizes[product.product_id] = "Free-NA";
        }

        let currentQty = product.quantity ?? 1;
        let unitPrice = bundle.isIndividualProduct
          ? product.unit_price
          : product.bundle_price;

        if (cartItem?.products?.length > 0) {
          const cartProduct = cartItem.products.find(
            (p) => p.productId === product.product_id
          );

          if (cartProduct) {
            currentQty = cartProduct.totalQuantity;
            unitPrice = bundle.isIndividualProduct
              ? cartProduct.unitPrice || product.unit_price
              : cartProduct.bundle_price || product.bundle_price;
          }
        }

        initialQuantities[product.product_id] = currentQty;
        bundlePrice += currentQty * unitPrice;

        products.push({
          cart_item_id: cartItem?.id,
          bundle_id: bundle.bundle_id,
          product_id: product.product_id,
          size: initialSizes[product.product_id],
          base_quantity: product.quantity ?? 0,
          addon_quantity: Math.max(0, currentQty - (product.quantity ?? 0)),
          total_quantity: currentQty,
          quantity: currentQty,
          unit_price: product.unit_price,
          bundle_price: product.bundle_price,
          is_individual_product: bundle.is_individual_product,
        });
      });

      setBundleTotalPrice(bundlePrice);
      setQuantities(initialQuantities);
      setCartItemProducts(products);
      setProductSizes(initialSizes);
    }, [bundle, cartData?.items, studentId]);

  useEffect(() => {
    if (user?.id) {
      dispatch(getCartDetail(user.id));
    }
  }, [user?.id, dispatch]);

  useEffect(() => {
    if (isAdminSession) {
      const loadProducts = async () => {
        try {
          const response = await fetchAllProducts();
          setAllProducts(Array.isArray(response) ? response : []);
        } catch (error) {
          console.error("Failed to load products:", error);
        }
      };
      loadProducts();
    }
  }, [isAdminSession]);

  const handleRemoveProduct = (productId) => {
    setBundle((prev) => ({
      ...prev,
      products: prev.products.filter((p) => p.product_id !== productId),
    }));
    toast.info("Product removed from selection");
  };

  const handleAddProduct = (selectedOption) => {
    if (!selectedOption) return;
    
    const productId = selectedOption.value;
    
    // Check if already in bundle
    if (bundle.products.some(p => p.product_id === productId)) {
      toast.warning("Product is already in the selection");
      return;
    }

    const productToAdd = allProducts.find(p => p.id === productId);
    if (!productToAdd) return;

    const newProduct = {
      product_id: productToAdd.id,
      product_name: productToAdd.name,
      product_display_name: productToAdd.display_name,
      unit_price: parseFloat(productToAdd.price),
      bundle_price: parseFloat(productToAdd.bundle_price),
      quantity: 1,
      max_quantity: 10,
      order: 99,
      optional: true,
      selection_group: false,
      size_chart: productToAdd.sizeChart,
      product_category: productToAdd.category,
      product_category_order: productToAdd.category_order,
      availableSizes: productToAdd.availableSizes && productToAdd.availableSizes.length > 0 
        ? productToAdd.availableSizes 
        : ["Free-NA"]
    };

    setBundle(prev => ({
      ...prev,
      products: [...(prev.products || []), newProduct]
    }));
    
    setShowAddProduct(false);
    toast.success("Product added to selection");
  };

  if (loading) return <FullPageSpinner loading={loading} />;
  if (!bundle) {
    return (
      <div className="bundle-details-container d-flex align-items-center justify-content-center">
        <div className="text-center p-5 shadow-sm rounded-4 bg-white" style={{ maxWidth: "450px", width: "100%" }}>
          <Package className="text-muted opacity-25 mb-4" size={80} />
          <h3 className="fw-bold text-dark mb-3">Bundle Not Found</h3>
          <p className="text-muted mb-4">
            The bundle you’re looking for doesn’t exist or may have been removed.
          </p>
          <button
            className="btn btn-primary rounded-pill px-4 py-2 fw-bold"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft size={18} className="me-2" />
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const handleAddToCart = () => {
    const productsMissingSize = cartItemProducts.filter(p => {
      const qty = Number(p.total_quantity) || 0;
      if (qty === 0) return false;
      const currentSize = pendingSizes[p.product_id] || p.size;
      return !currentSize;
    });

    if (productsMissingSize.length > 0) {
      toast.error("Please select sizes for all products", { position: "top-right" });
      return;
    }

    const maxWeight = getMaxConfiguredWeight(user?.settings);
    if (maxWeight > 0) {
      const existingItems = (cartData?.items || []).filter(
        (it) =>
          !(
            Number(it.bundleId) === Number(bundle?.bundle_id) &&
            Number(it.studentId || it.student?.id) === Number(studentId)
          )
      );
      const currentCartWeight = calculateCartWeight(existingItems).totalKg;
      const itemWeight = calculateItemAdditionWeight(
        bundle,
        cartItemProducts,
        quantity
      ).kg;
      const projectedWeight = Number((currentCartWeight + itemWeight).toFixed(2));
      if (projectedWeight > maxWeight) {
        toast.error(
          `Cannot add to cart: Total cart weight (${projectedWeight} KG) will exceed the maximum allowed limit of ${maxWeight} KG.`,
          { position: "top-right", autoClose: 5000 }
        );
        return;
      }
    }

    setShowConfirmModal(true);
  };

  const confirmAddToCart = async () => {
    setShowConfirmModal(false);
    dispatch(loadingCartChange(true));
    try {
      const body = {
        bundleId: bundle.bundle_id,
        quantity,
        parentId: user.id,
        studentId,
        cartItemProducts
      };
      setLoading(true);
      await dispatch(addToCart(body));
      setLoading(false);
      toast.success("Product added to cart successfully!");
      navigate("/cart")
    } catch (error) {
      toast.error(error?.message || "Failed to add to cart");
      setLoading(false);
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } finally {
      dispatch(loadingCartChange(false));
    }
  };

  const handleDownloadImage = () => {
    const link = document.createElement('a');
    link.href = bannerImage;
    link.download = 'Thathva-washcare-instructions.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleViewSizeChart = (sizeChart) => {
    window.open(sizeChart, '_blank');
  };

  const handleSizeSelect = async (productId, size) => {
    setPendingSizes(prev => ({ ...prev, [productId]: size }));
    await updateProductSize({ size, studentId, productId });
  };


  let groupedProducts = bundle?.products?.reduce((acc, item) => {
    const categoryName = item.product_category || "Others";
    const order = item.product_category_order ?? 999;

    if (!acc[categoryName]) {
      acc[categoryName] = { order, items: [] };
    }
    acc[categoryName].items.push(item);
    return acc;
  }, {}) || {};

  Object.values(groupedProducts).forEach((group) => {
    group.items.sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
  });

  const handleAddonChange = (item, value) => {
    const numericValue = Number(value) || 0;
    const minAllowed = isAdminSession || item.selection_group || item.optional ? 0 : item.quantity;
    const finalValue = Math.min(item.max_quantity, Math.max(minAllowed, numericValue));

    let updatedQuantities = { ...quantities, [item.product_id]: finalValue };

    if (item.selection_group && !isAdminSession) {
      const groupItems = bundle.products?.filter(p => p.selection_group === true);
      const groupTotal = groupItems.reduce((sum, p) => {
        const qty = p.product_id === item.product_id ? finalValue : updatedQuantities[p.product_id] ?? p.quantity;
        return sum + qty;
      }, 0);

      if (groupTotal < 1) {
        const fallbackItem = groupItems.find(p => p.product_id !== item.product_id);
        if (fallbackItem) updatedQuantities[fallbackItem.product_id] = fallbackItem.quantity;
        toast.error("Please select one product between Black Sweat Shirt/Yellow Puff Jacket.");
      }
    }

    setQuantities(updatedQuantities);
    setCartItemProducts((prev) => {
      const updated = prev.map((p) => {
        const qty = updatedQuantities[p.product_id] ?? p.total_quantity;
        return {
          ...p,
          total_quantity: qty,
          addon_quantity: Math.max(0, qty - p.base_quantity),
        };
      });

      let totalPrice = 0;
      updated.forEach((p) => {
        const price = p.is_individual_product ? p.unit_price : p.bundle_price;
        totalPrice += p.total_quantity * price;
      });
      setBundleTotalPrice(totalPrice);
      return updated;
    });
  };

  const isProductInCart = cartData?.items?.some(item => item.bundleId === bundle.bundle_id && item.studentId === Number(studentId));

  const handleRemove = () => {
    dispatch(loadingCartChange(true));
    dispatch(deleteCartItem({ parentId: user.id, bundleId: bundle.bundle_id }))
      .then(() => {
        toast.info("Item removed from cart!");
        setRemoveItemId(null);
      })
      .catch(() => toast.error("Failed to remove item from cart!"));
  };

  return (
    <div className="bundle-details-container py-4">
      <div className="container">
        <div className="bundle-card shadow-lg mb-4">
          <div className="bundle-header d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
            <div>
              <h2 className="fw-bold text-dark mb-1 fs-3">{bundle.student?.studentName}</h2>
              {/* <p className="text-muted small mb-0 d-flex align-items-center">
                <Package size={14} className="me-2" />
                {bundle.bundle_name ?? "Product Bundle"}
              </p> */}
            </div>
            <button className="btn btn-outline-secondary rounded-pill px-4 d-flex align-items-center" onClick={() => navigate(-1)}>
              <ArrowLeft size={18} className="me-2" />
              Back to Products
            </button>
          </div>

          <div className="card-body p-3 p-md-5">
            <div className="row g-5">
              <div className="col-lg-5">
                <div className="bundle-image-container mb-4">
                  <img
                    src={bundle.image}
                    className="img-fluid rounded-4 shadow-sm w-100"
                    alt={bundle.bundle_name}
                    style={{ maxHeight: '450px', objectFit: 'cover' }}
                  />
                </div>

                <div className="student-info-card">
                  <h6 className="fw-bold text-primary d-flex align-items-center mb-4">
                    <Info size={18} className="me-2" />
                    Student Details
                  </h6>
                  <div className="row g-4">
                    <div className="col-12 border-bottom pb-3">
                      <div className="info-label d-flex align-items-center"><User size={12} className="me-1" /> Full Name</div>
                      <div className="info-value fs-5">{bundle.student?.studentName}</div>
                    </div>
                    <div className="col-6">
                      <div className="info-label d-flex align-items-center">Gender</div>
                      <div className="info-value">{bundle.gender}</div>
                    </div>
                    <div className="col-6">
                      <div className="info-label d-flex align-items-center">  <GraduationCap size={12} className="me-1" />  <span>Class</span></div>
                      <div className="info-value">{bundle.student.class}</div>
                    </div>
                    <div className="col-6">
                      <div className="info-label d-flex align-items-center"><Home size={12} className="me-1" /> House</div>
                      <div className="info-value">{bundle.student?.house || 'N/A'}</div>
                    </div>
                    <div className="col-6">
                      <div className="info-label d-flex align-items-center"><Calendar size={12} className="me-1" /> Session</div>
                      <div className="info-value">{bundle.academic_year}</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-lg-7">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h5 className="fw-bold text-dark mb-0 d-flex align-items-center">
                    <ShoppingCart size={20} className="me-2 text-primary" />
                    Bundle Selection
                  </h5>
                  {isAdminSession && (
                    <button 
                      className="btn btn-success btn-sm rounded-pill px-3 d-flex align-items-center gap-2"
                      onClick={() => setShowAddProduct(!showAddProduct)}
                    >
                      <Plus size={16} />
                      {showAddProduct ? "Cancel Add" : "Add Product"}
                    </button>
                  )}
                </div>

                {showAddProduct && (
                  <div className="mb-4 p-3 border rounded-4 bg-light shadow-sm">
                    <label className="form-label fw-bold small text-muted">SEARCH & ADD PRODUCT</label>
                    <Select
                      options={allProducts.map(p => ({
                        value: p.id,
                        label: `${p.name} (${p.display_name}) - ₹${p.price}`
                      }))}
                      onChange={handleAddProduct}
                      placeholder="Type product name..."
                      isSearchable
                      className="mb-2"
                    />
                  </div>
                )}

                <div className="product-table-container mb-4">
                  <table className="table product-table mb-0">
                    <thead>
                      <tr>
                        <th>Product Details</th>
                        <th className="text-center">Size Guide</th>
                        <th className="text-center">Quantity</th>
                        <th className="text-end">Subtotal</th>
                        {bundle.isAlreadyPurchased ?(
                          <th className="py-3 px-4" style={{ width: '100px' }}>Size</th>
                        ):(
                          <th className="py-3 px-4" style={{ width: '200px' }}>Select Size</th>
                        )}
                        {isAdminSession && <th className="text-center">Action</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {bundle && bundle.products && Object.entries(groupedProducts)
                        .sort(([, a], [, b]) => a.order - b.order)
                        .map(([category, data]) => (
                          <React.Fragment key={category}>
                            {data.items.map((item, index) => (
                              <tr key={index}>
                                <td data-label="Product">
                                  <div className="product-name">{item.product_display_name ?? item.product_name}</div>
                                </td>
                                <td className="text-center" data-label="Guide">
                                  {item.size_chart && (
                                    <button className="btn btn-link text-primary btn-sm p-0 d-flex align-items-center justify-content-center w-100" onClick={() => handleViewSizeChart(item.size_chart)}>
                                      Size Chart
                                    </button>
                                  )}
                                </td>
                                <td data-label="Quantity">
                                  <div className="quantity-control">
                                    <button
                                      className="qty-btn"
                                      onClick={() => handleAddonChange(item, (quantities[item.product_id] || item.quantity) - 1)}
                                      disabled={(quantities[item.product_id] || item.quantity) <= (isAdminSession || item.selection_group || item.optional ? 0 : item.quantity)}
                                    >
                                      <Minus size={14} />
                                    </button>
                                    <span className="qty-value">{quantities[item.product_id] ?? item.quantity}</span>
                                    <button
                                      className="qty-btn"
                                      onClick={() => handleAddonChange(item, ((quantities[item.product_id] === 0 ? 0 : (quantities[item.product_id] ?? item.quantity)) + 1))}
                                      disabled={(quantities[item.product_id] || item.quantity) >= item.max_quantity}
                                    >
                                      <Plus size={14} />
                                    </button>
                                  </div>
                                </td>
                                <td className="text-end fw-bold" data-label="Subtotal">
                                  ₹{((quantities[item.product_id] ?? item.quantity) * (bundle.isIndividualProduct ? item.unit_price : item.bundle_price)).toLocaleString('en-IN')}
                                </td>
                                <td className="px-4" data-label="Size">
                                  {bundle.isAlreadyPurchased ? (
                                    <div className="fw-bold text-center">{pendingSizes[item.product_id] || productSizes[item.product_id] || ""}</div>
                                  ) : (
                                    <select
                                      className="form-select size-select-custom"
                                      value={pendingSizes[item.product_id] || productSizes[item.product_id] || ""}
                                      onChange={(e) => handleSizeSelect(item.product_id, e.target.value)}
                                    >
                                      <option value="">Select Size</option>
                                      {/* <option value="">Select Size</option>
                                      {item.availableSizes?.sort((a, b) => a.localeCompare(b, undefined, { numeric: true })).map(size => (
                                        <option key={size} value={size}>{size}</option>
                                      ))} */}

                                      {item.availableSizes
                                        ?.sort((a, b) => {
                                          const parseSize = (size) => {
                                            // Standard sizes order
                                            const standardOrder = {
                                              XS: 1,
                                              S: 2,
                                              M: 3,
                                              L: 4,
                                              XL: 5,
                                              '2XL': 6,
                                              '3XL': 7,
                                              '4XL': 8,
                                              '5XL': 9,
                                            };

                                            if (standardOrder[size]) {
                                              return { type: 'standard', order: standardOrder[size] };
                                            }

                                            // Numeric sizes like 4, 4L, 4S, 4XL
                                            const match = size.match(/^(\d+)([A-Z]*)$/);

                                            if (match) {
                                              const num = parseInt(match[1], 10);
                                              const suffix = match[2] || '';

                                              const suffixOrder = {
                                                '': 1,
                                                S: 2,
                                                L: 3,
                                                XL: 4,
                                                XXL: 5,
                                              };

                                              return {
                                                type: 'numeric',
                                                num,
                                                suffixOrder: suffixOrder[suffix] || 99,
                                              };
                                            }

                                            return { type: 'other', order: 999 };
                                          };

                                          const sizeA = parseSize(a);
                                          const sizeB = parseSize(b);

                                          // Standard sizes first
                                          if (sizeA.type === 'standard' && sizeB.type === 'standard') {
                                            return sizeA.order - sizeB.order;
                                          }

                                          // Numeric sizes
                                          if (sizeA.type === 'numeric' && sizeB.type === 'numeric') {
                                            if (sizeA.num !== sizeB.num) {
                                              return sizeA.num - sizeB.num;
                                            }
                                            return sizeA.suffixOrder - sizeB.suffixOrder;
                                          }

                                          // Standard before numeric (optional: change if needed)
                                          if (sizeA.type === 'standard') return -1;
                                          if (sizeB.type === 'standard') return 1;

                                          return 0;
                                        })
                                        .map((size) => (
                                          <option key={size} value={size}>
                                            {size}
                                          </option>
                                        ))}
                                    </select>
                                  )}
                                </td>
                                {isAdminSession && (
                                  <td className="text-center" data-label="Action">
                                    <button 
                                      className="btn btn-outline-danger btn-sm rounded-circle border-0"
                                      onClick={() => handleRemoveProduct(item.product_id)}
                                      title="Remove Product"
                                    >
                                      <Trash2 size={16} />
                                    </button>
                                  </td>
                                )}
                              </tr>
                            ))}
                          </React.Fragment>
                        ))}
                    </tbody>
                  </table>
                </div>

                <div className="summary-card shadow-sm">
                  <div className="row align-items-center">
                    <div className="col-md-7">
                      <div className="total-price-label">Order Total Amount</div>
                      <div className="total-price-value">₹{bundleTotalPrice.toLocaleString('en-IN')}</div>
                      <p className="text-muted small mb-0 mt-2 d-flex align-items-center">
                        <CheckCircle size={14} className="me-2 text-success" />
                        Includes all applicable taxes
                      </p>
                    </div>
                    <div className="col-md-5 mt-4 mt-md-0">
                      {bundle.isAlreadyPurchased && !bundle.allowRepurchase ? (
                        <button className="btn btn-secondary btn-lg w-100 rounded-pill d-flex align-items-center justify-content-center gap-2" disabled>
                          <CheckCircle size={20} /> Already Purchased
                        </button>
                      ) : (
                        <button
                          className="btn btn-primary action-btn-lg w-100 rounded-pill d-flex align-items-center justify-content-center gap-2"
                          onClick={handleAddToCart}
                        >
                          <ShoppingCart size={20} />
                          {isProductInCart ? "Update Order" : "Place Order"}
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mt-5 text-center">
                  <button className="washcare-btn shadow-sm d-flex align-items-center" onClick={handleDownloadImage}>
                    <Download size={16} className="me-2 text-primary" />
                    Download Washcare Instructions
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ConfirmModal
        show={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={confirmAddToCart}
        title="Verify Selection"
        message={`Please review your <strong>sizes and quantities</strong> before proceeding. 
            There are <span class="text-danger"><strong>no exchanges or returns</strong></span> permitted after submission.`}
        confirmButtonText="Confirm & Checkout"
        cancelButtonText="Review Selection"
      />

      {removeItemId && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg rounded-4 overflow-hidden">
              <div className="modal-header border-0 pb-0">
                <h5 className="fw-bold text-dark">Remove Bundle</h5>
                <button className="btn-close" onClick={() => setRemoveItemId(null)}></button>
              </div>
              <div className="modal-body py-4 text-center">
                <Trash2 size={64} className="text-danger opacity-75 mb-3" />
                <p className="fs-5 text-muted mb-0">Are you sure you want to remove this bundle from your cart?</p>
              </div>
              <div className="modal-footer border-0 pt-0">
                <button className="btn btn-light rounded-pill px-4" onClick={() => setRemoveItemId(null)}>Cancel</button>
                <button className="btn btn-danger rounded-pill px-4" onClick={handleRemove}>Yes, Remove</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BundleDetailsPage;
